export interface MilitaryConsultant {
  readonly name: string;
  readonly initials: string;
  readonly role: string;
  readonly phone: string;
  readonly email: string;
}

export interface SurveyQuestion {
  readonly id: string;
  readonly question: string;
}

export interface InfoCardContent {
  readonly title: string;
  readonly body: string;
}

export interface SurveySubmission {
  readonly visitorName: string;
  readonly consultantName: string;
  readonly responses: ReadonlyArray<{
    readonly questionId: string;
    readonly answer: 'yes' | 'no';
  }>;
  readonly timestamp: string;
}

// Currently routing all initial consultations to Ella per Jonathan Goffe 04/24/2026
// Ella will reassign to Frederique or Shondu as needed
// Full randomizer flow preserved below for future implementation
export const MILITARY_CONSULTANTS: ReadonlyArray<MilitaryConsultant> = [
  {
    name: 'Ella Rice',
    initials: 'ER',
    role: 'NEATT Manager',
    phone: '8029990323',
    email: 'Ella.Rice@ClientVitals.com',
  },
  // {
  //   name: 'Frederique',
  //   initials: 'FR',
  //   role: 'NEATT Consultant',
  //   phone: '8029990323',
  //   email: 'frederique@clientvitals.com',
  // },
  // {
  //   name: 'Shondu',
  //   initials: 'SH',
  //   role: 'NEATT Client Specialist',
  //   phone: '8029990323',
  //   email: 'Shondu@ClientVitals.com',
  // },
];

export const SURVEY_QUESTIONS: ReadonlyArray<SurveyQuestion> = [
  {
    id: 'loan_denial',
    question: 'Have you been denied for a loan in the last 2 years?',
  },
  {
    id: 'credit_knowledge',
    question: 'Do you have an understanding of how a credit score is calculated?',
  },
  {
    id: 'hardship',
    question: 'Have you experienced a hardship that has impacted your credit?',
  },
];

export const CERA_INFO: InfoCardContent = {
  title: 'What is CERA?',
  body: 'Consumer Eligibility Roadmap Assistant. CERA analyzes your full credit profile, identifies the specific factors holding back your score, and returns a detailed action plan showing exactly what you need to do to improve your credit. CERA is built on 20 years of real credit counseling expertise.',
};

export const MAP_INFO: InfoCardContent = {
  title: 'What is a MAP report?',
  body: 'Multi Agency Preview. Your MAP report gives you an overview of your credit picture across all three bureaus (Equifax, TransUnion, and Experian) with customizable parameters and proactive alerts, so your credit never gets on top of you.',
};

export const HERO_TRUST_PILLS: ReadonlyArray<string> = [
  'Soft pull only, will not affect your score',
  'Real FICO score, what lenders use',
  'Nationwide, M-F 9am to 5pm EST',
];

export const MILITARY_PAGE_COPY = {
  heroHeadline: 'Your credit score opens doors. We help you get it there.',
  heroSubheadline:
    'We help military personnel identify exactly what is holding their credit back and fix it with a real action plan, a real consultant, and tools built for this.',
  urgencyBoldTerms: 'VA loans, base housing, and PCS moves',
  urgencyMessage:
    'all depend on your credit profile. Most denials are fixable, and we give you exactly what you need to fix yours.',
  whatWeAreHeadline: 'A nationwide pre-qualification firm built around you',
  whatWeAreBody:
    'We are not a bank and we are not a lender. We are a consumer-focused firm that specializes in removing the roadblocks standing between you and loan approval, with real action plans, real roadmaps, and a real consultant working with you every step of the way. We service the full United States and territories, with consultants on both coasts.',
  ceraIntro: 'Our pre-qualification experts, assisted by our in-house AI tool',
  ceraMapBridge: ', analyze your credit and build your',
  ceraMapOutro:
    'showing you exactly what is holding your score back and what to do about it.',
  softPullBadge: 'We only pull soft credit reports. Your score is never affected.',
  valueHeadline: '$5 a month. $125 worth of credit analysis.',
  valueBody:
    'Most people pay hundreds for the kind of insight ClientVitals delivers automatically. For as little as $5 a month you get professional-grade FICO reporting, the same score your lender actually uses, not the estimate apps like Credit Karma show you.',
  bronzeLabel: 'Bronze, starting price',
  bronzeValue: 'monthly value you receive',
  ficoLabel: 'Real FICO',
  ficoSubLabel: 'The score your lender sees',
  roiLabel: '25x',
  roiSubLabel: 'Return on your subscription',
  goldPlanLabel: 'Gold plan at $150/month',
  goldValueNote:
    'A single credit counseling session can cost $300 or more. For $150 a month the Gold plan gives you access to an experienced consultant backed by CERA, our AI tool built on 20 years of real credit counseling expertise. Together they analyze your full credit picture and build you a personalized action plan that no generic service can match. Most clients see measurable improvement within 90 days just by following a targeted plan built on what has worked for hundreds of real clients before them. Many are done in a single subscription cycle.',
  viewAllPlans: 'View all plans',
  howItWorksHeadline: 'Three steps to a stronger credit profile',
  whyUsHeadline: 'Not a bandage. A real fix.',
  consultantHeadline: 'A real person, ready to talk',
  consultantSubheadline: 'One of our consultants is ready to help you today.',
  consultantAvailability: 'Available nationwide, M-F 9am to 5pm EST',
  connectHeadline: 'Our consultants have helped hundreds of people just like you',
  connectBody:
    'Whatever brought you here, we have the tools and the people to help you move forward. No pressure, no obligation. Just a real conversation.',
  connectButtonLabel: 'Connect with a consultant',
  surveyIntro: 'To help our consultants serve you best, please answer a few quick questions.',
  callLabel: 'Call',
  textLabel: 'Text',
  emailLabel: 'Email',
  footerContact: 'ClientVitals, info@clientvitals.com, (802) 999-0323',
  footerLocation: 'Vermont-based, serving the full United States and territories',
  privacyPolicy: 'Privacy Policy',
  termsOfUse: 'Terms of Use',
} as const;

export const MILITARY_PAGE_STEPS = [
  {
    number: 1,
    title: 'Create an account and choose a plan',
    body: 'View all plans to find the right fit. For as little as $5 a month you get $125 in professional-grade credit analysis from day one.',
  },
  {
    number: 2,
    title: 'Complete your onboarding modules',
    body: 'Short guided steps that give us the full picture to build your MAP report.',
  },
  {
    number: 3,
    title: 'Review your MAP and take action',
    body: 'Your consultant walks you through your report and your action plan to clear the path to loan approval.',
  },
] as const;

export const MILITARY_PAGE_FEATURES = [
  'We work with you, not for you. You stay in control.',
  'The real FICO score, the same one your lender sees, not an estimate.',
  'Soft pull only. Checking your credit with us never lowers your score.',
  'AI-driven roadmap built on 20 years of real credit counseling expertise.',
  'Virtual face-to-face consultant sessions available.',
  'Nationwide coverage with consultants on both coasts.',
  'Weekend hours available.',
] as const;

export const PRIVACY_POLICY_URL =
  'https://www.dropbox.com/scl/fi/16gh8d8js25cyjajh04vn/Privacy-Policy.pdf?rlkey=wm8ah4j9t7f1t5fnmuffi7df0&st=nz4jr1hq&dl=0';

export const TERMS_URL =
  'https://www.dropbox.com/scl/fi/ueb58s7eli31z6jfry3ke/ToS.pdf?rlkey=nq3erxn19cj0x808l7fapfb7t&st=aaexcp2s&dl=0';

export const PRICING_URL = '/client-tiers';
export const GENERAL_PHONE = '8029990323';
export const GENERAL_EMAIL = 'info@clientvitals.com';

